import { useState } from 'react'
import {
  ChevronDown,
  ChevronUp,
  Copy,
  CheckCircle,
  Hash,
  AtSign,
  Link as LinkIcon,
  Image as ImageIcon,
} from 'lucide-react'

interface SocialPreviewProps {
  hashtags?: string[] | null
  mentions?: string[] | null
  links?: Array<{ url: string; text?: string }> | null
  imagePrompts?: Array<{ prompt: string; style?: string }> | null
  channel?: string
}

export function SocialPreview({
  hashtags,
  mentions,
  links,
  imagePrompts,
  channel,
}: SocialPreviewProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [copiedItem, setCopiedItem] = useState<string | null>(null)

  const hasContent =
    (hashtags && hashtags.length > 0) ||
    (mentions && mentions.length > 0) ||
    (links && links.length > 0) ||
    (imagePrompts && imagePrompts.length > 0)

  if (!hasContent) return null

  const copyText = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedItem(id)
      setTimeout(() => setCopiedItem(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const copyAllHashtags = () => {
    if (hashtags) {
      copyText(hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' '), 'all-hashtags')
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-700">Social & Media</span>
          {channel && (
            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">
              {channel}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100">
          {/* Hashtags */}
          {hashtags && hashtags.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-500 font-medium flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  Hashtags ({hashtags.length})
                </label>
                <button
                  onClick={copyAllHashtags}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                >
                  {copiedItem === 'all-hashtags' ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Copiados
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copiar todos
                    </>
                  )}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {hashtags.map((tag, i) => {
                  const displayTag = tag.startsWith('#') ? tag : `#${tag}`
                  return (
                    <button
                      key={i}
                      onClick={() => copyText(displayTag, `tag-${i}`)}
                      className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors cursor-pointer"
                      title="Click para copiar"
                    >
                      {copiedItem === `tag-${i}` ? (
                        <span className="text-emerald-600">Copiado</span>
                      ) : (
                        displayTag
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Mentions */}
          {mentions && mentions.length > 0 && (
            <div>
              <label className="text-xs text-slate-500 font-medium flex items-center gap-1 mb-2">
                <AtSign className="w-3 h-3" />
                Menciones ({mentions.length})
              </label>
              <div className="flex flex-wrap gap-1.5">
                {mentions.map((mention, i) => {
                  const displayMention = mention.startsWith('@') ? mention : `@${mention}`
                  return (
                    <button
                      key={i}
                      onClick={() => copyText(displayMention, `mention-${i}`)}
                      className="px-2.5 py-1 bg-violet-50 text-violet-700 rounded-lg text-xs font-medium hover:bg-violet-100 transition-colors cursor-pointer"
                      title="Click para copiar"
                    >
                      {copiedItem === `mention-${i}` ? (
                        <span className="text-emerald-600">Copiado</span>
                      ) : (
                        displayMention
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Links */}
          {links && links.length > 0 && (
            <div>
              <label className="text-xs text-slate-500 font-medium flex items-center gap-1 mb-2">
                <LinkIcon className="w-3 h-3" />
                Enlaces ({links.length})
              </label>
              <div className="space-y-1.5">
                {links.map((link, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      {link.text && (
                        <p className="text-xs font-medium text-slate-700 truncate">{link.text}</p>
                      )}
                      <p className="text-xs text-slate-500 truncate font-mono">{link.url}</p>
                    </div>
                    <button
                      onClick={() => copyText(link.url, `link-${i}`)}
                      className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0 ml-2"
                    >
                      {copiedItem === `link-${i}` ? (
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Image Prompts */}
          {imagePrompts && imagePrompts.length > 0 && (
            <div>
              <label className="text-xs text-slate-500 font-medium flex items-center gap-1 mb-2">
                <ImageIcon className="w-3 h-3" />
                Prompts Visuales ({imagePrompts.length})
              </label>
              <div className="space-y-1.5">
                {imagePrompts.map((prompt, i) => (
                  <div
                    key={i}
                    className="flex items-start justify-between bg-amber-50 border border-amber-100 rounded-lg px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-slate-700 leading-relaxed">{prompt.prompt}</p>
                      {prompt.style && (
                        <p className="text-xs text-amber-600 mt-1">Estilo: {prompt.style}</p>
                      )}
                    </div>
                    <button
                      onClick={() => copyText(prompt.prompt, `prompt-${i}`)}
                      className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0 ml-2"
                    >
                      {copiedItem === `prompt-${i}` ? (
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
